import { useCallback, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faKey,
  faCopy,
  faCheckCircle,
  faTriangleExclamation,
  faDownload,
  faPlus,
  faBan,
  faTrash,
  faUserShield,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons'
import { apiKeysApi } from '../../services/api/apiKeysApi'
import { useAuth } from '../../hooks/useAuth'
import type {
  ApiKey,
  ApiKeyCreateResponse,
  ApiKeyListResponse,
  ApiKeyPermission,
  ApiKeyRequest,
  SubmitApiKeyRequest,
} from '../../types/apiKeys'

const EXPIRY_CHOICES = [30, 60, 90, 180, 365]

/** Days until `iso`, rounded up. Negative once the date has passed. */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / (24 * 60 * 60 * 1000))
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Expiry as a human reads it. The warning threshold matters more than the date
 * itself — an integration that dies unannounced is the failure we're avoiding.
 */
function ExpiryCell({ apiKey }: { apiKey: ApiKey }) {
  if (apiKey.never_expires) {
    return <span className="text-gray-500">Never</span>
  }
  if (!apiKey.expires_at) return <span className="text-gray-500">—</span>

  const days = daysUntil(apiKey.expires_at)
  const isSoon = days > 0 && days <= 30

  return (
    <div>
      <div className={isSoon ? 'text-amber-700 font-medium' : 'text-gray-700'}>
        {formatDate(apiKey.expires_at)}
      </div>
      {isSoon && <div className="text-xs text-amber-600">in {days} day{days === 1 ? '' : 's'}</div>}
    </div>
  )
}

function StatusChip({ apiKey }: { apiKey: ApiKey }) {
  const styles: Record<string, string> = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-gray-200 text-gray-700',
    revoked: 'bg-red-100 text-red-800',
    deleted: 'bg-gray-100 text-gray-500 line-through',
  }
  const label =
    apiKey.status === 'active' && apiKey.never_expires ? 'Active · Perpetual' : apiKey.status

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${
        styles[apiKey.status] ?? styles.expired
      }`}
    >
      {label}
    </span>
  )
}

/**
 * Show-once modal.
 *
 * Deliberately not dismissible by Escape or backdrop click, and Close stays
 * disabled until the user confirms they've stored the key. The key cannot be
 * retrieved again, so the friction here is the point — an accidental dismissal
 * costs the user a full regenerate-and-redeploy cycle.
 */
function NewKeyModal({
  created,
  onClose,
}: {
  created: ApiKeyCreateResponse
  onClose: () => void
}) {
  const [confirmed, setConfirmed] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(created.api_key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = () => {
    const body = `# IOAG Partner API credentials\n# ${created.label ?? 'API key'} — created ${formatDate(
      created.created_at
    )}\n# ${created.never_expires ? 'Never expires' : `Expires ${formatDate(created.expires_at)}`}\nIOAG_API_KEY=${created.api_key}\n`
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'ioag-api-key.env'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-key-title"
    >
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <h2 id="new-key-title" className="text-lg font-semibold text-gray-900 flex items-center">
          <FontAwesomeIcon icon={faTriangleExclamation} className="h-5 w-5 mr-2 text-amber-500" />
          Copy your API key now
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          This is the only time this key will be shown. It is stored hashed, so we cannot
          show it again — if you lose it you'll need to generate a replacement.
        </p>

        <code className="mt-4 block w-full break-all rounded border border-gray-300 bg-gray-50 px-4 py-3 font-mono text-sm">
          {created.api_key}
        </code>

        <div className="mt-3 flex gap-2">
          <button
            onClick={handleCopy}
            className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} className="h-4 w-4 mr-2" />
            {copied ? 'Copied' : 'Copy key'}
          </button>
          <button
            onClick={handleDownload}
            className="inline-flex items-center rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <FontAwesomeIcon icon={faDownload} className="h-4 w-4 mr-2" />
            Download .env
          </button>
        </div>

        <label className="mt-5 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
          />
          <span>I've stored this key somewhere safe.</span>
        </label>

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            disabled={!confirmed}
            className="rounded bg-gray-900 px-5 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

/** Revoke confirmation. Requires typing the key's identifier to arm the button. */
function RevokeModal({
  apiKey,
  onCancel,
  onConfirm,
  busy,
}: {
  apiKey: ApiKey
  onCancel: () => void
  onConfirm: () => void
  busy: boolean
}) {
  const [typed, setTyped] = useState('')
  const expected = apiKey.label || apiKey.preview

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Revoke this API key?</h2>
        <p className="mt-2 text-sm text-gray-600">
          Requests using this key will start failing immediately. Anything still
          configured with it will break until it's replaced.
        </p>

        {apiKey.issued_by_admin && (
          <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p className="font-medium">An IOAG administrator issued this key for you.</p>
            <p className="mt-1">
              {apiKey.justification
                ? `It was issued for: ${apiKey.justification}`
                : 'It usually backs an integration set up on your behalf.'}{' '}
              Revoking it will stop that integration, and only an administrator can
              issue a replacement.
            </p>
          </div>
        )}

        <dl className="mt-4 space-y-1 rounded bg-gray-50 p-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Key</dt>
            <dd className="font-mono">{apiKey.preview}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Label</dt>
            <dd>{apiKey.label ?? '—'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Last used</dt>
            <dd>{apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}</dd>
          </div>
        </dl>

        <label className="mt-4 block text-sm text-gray-700">
          Type <span className="font-mono font-semibold">{expected}</span> to confirm:
          <input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm"
            autoFocus
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={typed !== expected || busy}
            className="rounded bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {busy ? 'Revoking…' : 'Revoke key'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Ask an admin for a never-expiring key.
 *
 * The justification is mandatory and does real work: it is what the approving
 * admin decides on, and it is copied verbatim onto the key, so it is also what
 * the requester will later see explaining why the key exists.
 */
function RequestPerpetualDialog({
  onCancel,
  onSubmit,
  busy,
  error,
}: {
  onCancel: () => void
  onSubmit: (request: SubmitApiKeyRequest) => void
  busy: boolean
  error: string | null
}) {
  const [label, setLabel] = useState('')
  const [justification, setJustification] = useState('')
  const [permission, setPermission] = useState<ApiKeyPermission>('read_write')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-key-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="request-key-title" className="text-lg font-semibold text-gray-900">
          Request a non-expiring key
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Keys normally expire so that a leaked one stops working on its own. Where an
          integration genuinely cannot rotate, an administrator can issue a key with no
          expiry. Tell them what it's for and they'll review it.
        </p>

        {error && (
          <div className="mt-4 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <label className="mt-4 block text-sm text-gray-700">
          Label
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={64}
            placeholder="e.g. Field logger"
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="mt-4 block text-sm text-gray-700">
          Why can't this integration use an expiring key?
          <textarea
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="e.g. embedded data logger in the field with no update path"
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            autoFocus
          />
          <span className="mt-1 block text-xs text-gray-500">
            Recorded on the key, so anyone looking at it later knows what it's for.
          </span>
        </label>

        <label className="mt-4 block text-sm text-gray-700">
          Permissions
          <select
            value={permission}
            onChange={(e) => setPermission(e.target.value as ApiKeyPermission)}
            className="mt-1 block rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="read_write">Read &amp; write</option>
            <option value="read_only">Read only</option>
          </select>
        </label>

        <p className="mt-4 rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
          If approved, the key is shown once to the administrator who issues it — keys are
          never displayed twice, so they'll pass it to you directly rather than it
          appearing here.
        </p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onSubmit({
                label: label.trim() || undefined,
                justification: justification.trim(),
                permission,
              })
            }
            disabled={busy || justification.trim().length === 0}
            className="rounded bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {busy ? 'Sending…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Where an outstanding or recently-decided request stands.
 *
 * Rendered above the key list rather than inside it: a request is not a key,
 * and showing it as a row would imply something exists that can be used.
 */
function RequestStatusCard({
  request,
  onCancel,
  busy,
}: {
  request: ApiKeyRequest
  onCancel: () => void
  busy: boolean
}) {
  if (request.status === 'pending') {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-blue-900">
              <FontAwesomeIcon icon={faHourglassHalf} className="mr-2 h-4 w-4" />
              Non-expiring key request awaiting review
            </p>
            <p className="mt-1 text-sm text-blue-800">
              Sent {formatDate(request.requested_at)} for{' '}
              <span className="font-medium">{request.label || 'an unlabelled key'}</span>.
              An administrator will review it and pass you the key directly if approved.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="shrink-0 text-sm font-medium text-blue-700 underline hover:text-blue-900 disabled:text-blue-300"
          >
            {busy ? 'Withdrawing…' : 'Withdraw'}
          </button>
        </div>
      </div>
    )
  }

  if (request.status === 'denied') {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-medium text-amber-900">
          Your request for a non-expiring key was declined
        </p>
        <p className="mt-1 text-sm text-amber-800">
          {request.denial_reason ?? 'No reason was given.'}
        </p>
      </div>
    )
  }

  if (request.status === 'approved') {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <p className="text-sm font-medium text-green-900">
          <FontAwesomeIcon icon={faCheckCircle} className="mr-2 h-4 w-4" />
          Your non-expiring key was approved
        </p>
        <p className="mt-1 text-sm text-green-800">
          It appears in the list below, badged "Issued by IOAG". The key itself was shown
          once to the administrator who issued it — ask them for it if you don't have it.
        </p>
      </div>
    )
  }

  return null
}

/**
 * Shown to accounts that cannot own a key at all.
 *
 * The tab is already disabled for these roles, but the route is reachable by
 * typing it, and an empty list above a Generate button that always fails is a
 * worse answer than saying why.
 */
function KeysUnavailable({ reason }: { reason: string }) {
  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="flex items-center text-2xl font-bold text-gray-900">
          <FontAwesomeIcon icon={faKey} className="mr-2 h-6 w-6 text-blue-600" />
          API Keys
        </h1>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-gray-700">{reason}</p>
      </div>
    </div>
  )
}

export default function ApiKeys() {
  const { isPartner, isAdmin, isGuest } = useAuth()
  const [data, setData] = useState<ApiKeyListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Reach is identity, not configuration — it follows the account's role and
  // cannot be chosen, so it is stated rather than offered as a control. A
  // dropdown with one valid option would imply the others exist.
  const partner = isPartner()
  const reachLabel = partner ? 'Your fleet' : 'Your devices'
  const reachDetail = partner
    ? 'all devices you own, including ones assigned to your customers'
    : 'the devices registered to your account'

  const [label, setLabel] = useState('')
  const [expiryDays, setExpiryDays] = useState(90)
  const [permission, setPermission] = useState<ApiKeyPermission>('read_write')
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState<ApiKeyCreateResponse | null>(null)

  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const [requests, setRequests] = useState<ApiKeyRequest[]>([])
  const [requesting, setRequesting] = useState(false)
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [cancellingRequest, setCancellingRequest] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // One failure must not blank the other: a request-list outage should not
      // hide the keys the account actually holds.
      const [keys, requestList] = await Promise.all([
        apiKeysApi.list(),
        apiKeysApi.requests.list().catch(() => ({ requests: [], total: 0 })),
      ])
      setData(keys)
      setRequests(requestList.requests)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // After the hooks, never before — the early return must not change how many
  // hooks run between renders.
  if (isGuest()) {
    return (
      <KeysUnavailable reason="API keys are not available on guest accounts. Your access to each device is granted by its owner, so there is no set of devices a key of yours could reach." />
    )
  }
  if (isAdmin()) {
    return (
      <KeysUnavailable reason="API keys are not available on admin accounts. An admin reaches every device in the system, which is more than any long-lived credential should carry — use a non-admin service account for programmatic access." />
    )
  }

  const activeCount = data?.api_keys.filter((k) => k.status === 'active').length ?? 0
  const atLimit = data ? activeCount >= data.limits.max_active_keys : false

  // A pending request outranks any decided one — it is the thing the account is
  // waiting on. Otherwise show the most recent decision, and only while it is
  // still news: a denial from months ago is history, not a status.
  const pendingRequest = requests.find((r) => r.status === 'pending') ?? null
  const lastDecided = requests.find(
    (r) => r.status === 'denied' || r.status === 'approved'
  )
  const recentlyDecided =
    !pendingRequest && lastDecided && daysUntil(lastDecided.decided_at ?? '') > -30
      ? lastDecided
      : null
  const shownRequest = pendingRequest ?? recentlyDecided

  const handleCreate = async () => {
    setCreating(true)
    setError(null)
    try {
      const result = await apiKeysApi.create({
        label: label.trim() || undefined,
        expires_in_days: expiryDays,
        permission,
      })
      setCreated(result)
      setLabel('')
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate API key')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async (apiKey: ApiKey) => {
    // An admin-issued key gets the heavier warning, and names the integration
    // it was issued for — the owner did not set this one up, so "remove" is
    // more consequential than it looks from the list.
    const prompt = apiKey.issued_by_admin
      ? 'Remove this administrator-issued key?\n\n' +
        (apiKey.justification ? `It was issued for: ${apiKey.justification}\n\n` : '') +
        'It stops working immediately and that integration will break. Only an ' +
        'administrator can issue a replacement.'
      : 'Remove this key from your list?\n\n' +
        'It stops working immediately. The record is kept so an administrator ' +
        'can still see what it was used for.'

    if (!window.confirm(prompt)) {
      return
    }

    setDeletingId(apiKey.id)
    setError(null)
    try {
      await apiKeysApi.remove(apiKey.id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete API key')
    } finally {
      setDeletingId(null)
    }
  }

  const handleSubmitRequest = async (request: SubmitApiKeyRequest) => {
    setRequesting(true)
    setRequestError(null)
    try {
      await apiKeysApi.requests.create(request)
      setRequestDialogOpen(false)
      await load()
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Failed to send request')
    } finally {
      setRequesting(false)
    }
  }

  const handleCancelRequest = async (id: string) => {
    setCancellingRequest(true)
    setError(null)
    try {
      await apiKeysApi.requests.cancel(id)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw request')
    } finally {
      setCancellingRequest(false)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    setRevoking(true)
    setError(null)
    try {
      await apiKeysApi.revoke(revokeTarget.id)
      setRevokeTarget(null)
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke API key')
    } finally {
      setRevoking(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <h1 className="flex items-center text-2xl font-bold text-gray-900">
          <FontAwesomeIcon icon={faKey} className="mr-2 h-6 w-6 text-blue-600" />
          API Keys
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Generate keys so your own systems can call the IOAG API. A key reaches
          exactly what you can see in the app — nothing more.
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {shownRequest && (
        <RequestStatusCard
          request={shownRequest}
          busy={cancellingRequest}
          onCancel={() => handleCancelRequest(shownRequest.id)}
        />
      )}

      {/* Generate */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Generate a new key</h2>
        <div className="mt-4 flex flex-wrap items-end gap-4">
          <label className="flex-1 min-w-[200px] text-sm text-gray-700">
            Label
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={64}
              placeholder="e.g. Production ETL"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm text-gray-700">
            Expires in
            <select
              value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))}
              className="mt-1 block rounded border border-gray-300 px-3 py-2 text-sm"
            >
              {EXPIRY_CHOICES.map((days) => (
                <option key={days} value={days}>
                  {days} days
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-700">
            Permissions
            <select
              value={permission}
              onChange={(e) => setPermission(e.target.value as ApiKeyPermission)}
              className="mt-1 block rounded border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="read_write">Read &amp; write</option>
              <option value="read_only">Read only</option>
            </select>
          </label>
          <button
            onClick={handleCreate}
            disabled={creating || atLimit}
            className="inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2 h-4 w-4" />
            {creating ? 'Generating…' : 'Generate key'}
          </button>
        </div>
        {atLimit && (
          <p className="mt-3 text-sm text-amber-700">
            You have {activeCount} active keys, the maximum. Revoke one before generating another.
          </p>
        )}
        <div className="mt-3 space-y-1 text-xs text-gray-500">
          <p>
            <span className="font-medium text-gray-700">Permissions</span> set what this key
            can do. Read-only keys can fetch data but not change anything — a safer default
            for reporting or dashboards.
          </p>
          <p>
            <span className="font-medium text-gray-700">Reach</span> is set by your account,
            not by the key: <span className="font-medium">{reachDetail}</span>
          </p>
          <p>
            Need a key that never expires? Those are issued by an IOAG administrator, for
            integrations that genuinely can't rotate.{' '}
            {pendingRequest ? (
              <span className="text-gray-600">You have a request awaiting review.</span>
            ) : (
              <button
                onClick={() => {
                  setRequestError(null)
                  setRequestDialogOpen(true)
                }}
                className="font-medium text-blue-600 underline hover:text-blue-800"
              >
                Request one
              </button>
            )}
          </p>
        </div>
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Label', 'Key', 'Permissions', 'Reach', 'Status', 'Created', 'Expires', 'Last used', ''].map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && data?.api_keys.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-500">
                  No API keys yet. Generate one above to start calling the API.
                </td>
              </tr>
            )}
            {!loading &&
              data?.api_keys.map((apiKey) => (
                <tr key={apiKey.id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    <div>{apiKey.label ?? '—'}</div>
                    {apiKey.issued_by_admin && (
                      <span
                        className="mt-1 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
                        title={
                          apiKey.justification
                            ? `Issued by an IOAG administrator: ${apiKey.justification}`
                            : 'Issued by an IOAG administrator on your behalf'
                        }
                      >
                        <FontAwesomeIcon icon={faUserShield} className="mr-1 h-3 w-3" />
                        Issued by IOAG
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm text-gray-600">{apiKey.preview}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {apiKey.permission === 'read_only' ? 'Read only' : 'Read & write'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" title={reachDetail}>
                    {reachLabel}
                  </td>
                  <td className="px-4 py-3">
                    <StatusChip apiKey={apiKey} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(apiKey.created_at)}</td>
                  <td className="px-4 py-3 text-sm">
                    <ExpiryCell apiKey={apiKey} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {apiKey.status === 'active' && (
                      <button
                        onClick={() => setRevokeTarget(apiKey)}
                        className="mr-3 inline-flex items-center text-sm font-medium text-red-600 hover:text-red-800"
                      >
                        <FontAwesomeIcon icon={faBan} className="mr-1 h-4 w-4" />
                        Revoke
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(apiKey)}
                      disabled={deletingId === apiKey.id}
                      className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 disabled:text-gray-300"
                      title="Remove from this list. The key stops working; the record is kept."
                    >
                      <FontAwesomeIcon icon={faTrash} className="mr-1 h-4 w-4" />
                      {deletingId === apiKey.id ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {requestDialogOpen && (
        <RequestPerpetualDialog
          busy={requesting}
          error={requestError}
          onCancel={() => setRequestDialogOpen(false)}
          onSubmit={handleSubmitRequest}
        />
      )}

      {created && <NewKeyModal created={created} onClose={() => setCreated(null)} />}
      {revokeTarget && (
        <RevokeModal
          apiKey={revokeTarget}
          busy={revoking}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={handleRevoke}
        />
      )}
    </div>
  )
}
